import { useCallback, useRef } from 'react';
import { type NavigateFunction, type NavigateOptions, type To, useNavigate } from 'react-router-dom';

/**
 * `useNavigate` with a stable identity.
 *
 * Outside a data router (for example with `<BrowserRouter>`), React Router's `navigate` is a new function whenever
 * the pathname changes, so listing it in an effect or `useCallback` dependency array re-runs that effect on every
 * navigation. A lot of code does list it (for example `ProtectedRoute`'s sign-in effect and `useSaveOnExit`). This
 * wrapper keeps the latest `navigate` in a ref and returns one function that never changes, so those dependency
 * arrays behave as if `navigate` were constant. Under a data router, where `navigate` is already stable, it can go.
 *
 * Calls go through the latest `navigate`, so relative paths still resolve against the current route.
 */
const useAppNavigate = (): NavigateFunction => {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    // Arguments are forwarded untouched, so a caller sees `navigate(path)` rather than `navigate(path, undefined)`.
    const stableNavigate = useCallback((...args: [to: To | number, options?: NavigateOptions]): void => {
        (navigateRef.current as (...forwarded: typeof args) => void)(...args);
    }, []);

    // `NavigateFunction` is overloaded (a path, or a delta such as -1); the one implementation above handles both.
    return stableNavigate as NavigateFunction;
};

export default useAppNavigate;
